import React from 'react';

import type {
  FarmTypeOption,
  IFarmOverviewSubStepProps,
  OwnershipOption,
} from '../farmInformation.types';

export const FarmOverviewSubStep: React.FC<IFarmOverviewSubStepProps> = ({
  farmOverviewError,
  farmValidationErrors,
  farmBackgroundWordCount,
  farmName,
  farmBackground,
  farmDirections,
  farmOwnership,
  areaLeased,
  leaseEndDate,
  ownedHectares,
  farmType,
  areaUnderIrrigation,
  areaOfDryLand,
  selectedWaterSources,
  fullTimeFarming,
  shortTermGoals,
  longTermGoals,
  landComposition,
  workforceNumber,
  workerTrust,
  employmentBeforeAfter,
  onFarmNameChange,
  onFarmBackgroundChange,
  onFarmDirectionsChange,
  onFarmOwnershipChange,
  onOwnedHectaresChange,
  onAreaLeasedChange,
  onLeaseEndDateChange,
  onFarmTypeChange,
  onAreaUnderIrrigationChange,
  onAreaOfDryLandChange,
  onWaterSourceToggle,
  onFullTimeFarmingChange,
  onShortTermGoalsChange,
  onLongTermGoalsChange,
  onLandCompositionChange,
  onWorkforceNumberChange,
  onWorkerTrustChange,
  onEmploymentBeforeAfterChange,
  leaseYearOptions,
  yesNoOptions,
  waterSourceOptions,
}) => (
  <form className="farm-overview" onSubmit={(event) => event.preventDefault()}>
    <div className="form-grid">
      {farmOverviewError ? (
        <div className="form-alert is-error">{farmOverviewError}</div>
      ) : null}

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="farm-name">
          Farm name
        </label>
        <input
          id="farm-name"
          className={`money-form-field__control money-form-field__control--input${
            farmValidationErrors.farmName ? ' is-error' : ''
          }`}
          value={farmName}
          onChange={(event) => onFarmNameChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="farming-background">
          {`What is your farming background? (${farmBackgroundWordCount}/50 words minimum)`}
        </label>
        <textarea
          id="farming-background"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.farmBackground ? ' is-error' : ''
          }`}
          rows={4}
          value={farmBackground}
          onChange={(event) => onFarmBackgroundChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="farm-directions">
          Step by step directions to the farm?
        </label>
        <textarea
          id="farm-directions"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.farmDirections ? ' is-error' : ''
          }`}
          rows={3}
          value={farmDirections}
          onChange={(event) => onFarmDirectionsChange(event.target.value)}
        />
      </div>

      <fieldset
        className={`money-choice-group${
          farmValidationErrors.farmOwnership ? ' is-error' : ''
        }`}
      >
        <legend className="money-choice-group__legend">
          Is your farm owned or leased?
        </legend>
        <div className="money-choice-group__options money-choice-group__options--inline">
          {(['Leased', 'Owned', 'Both'] as OwnershipOption[]).map((option) => {
            const checked = farmOwnership === option;

            return (
              <label
                key={option}
                className={`money-choice money-choice--radio${
                  checked ? ' is-checked' : ''
                }`}
              >
                <input
                  type="radio"
                  className="money-choice__control"
                  name="farm-ownership"
                  value={option}
                  checked={checked}
                  onChange={() => onFarmOwnershipChange(option)}
                />
                <span className="money-choice__label-wrap">
                  <span className="money-choice__label">{option}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {(farmOwnership === 'Owned' || farmOwnership === 'Both') && (
        <div className="money-form-field">
          <label
            className="money-form-field__label"
            htmlFor="owned-hectares-input"
          >
            Owned hectares (ha)
          </label>
          <input
            id="owned-hectares-input"
            type="number"
            min="0"
            step="0.01"
            className={`money-form-field__control money-form-field__control--input${
              farmValidationErrors.ownedHectares ? ' is-error' : ''
            }`}
            value={ownedHectares}
            onChange={(event) => onOwnedHectaresChange(event.target.value)}
          />
        </div>
      )}

      {(farmOwnership === 'Leased' || farmOwnership === 'Both') && (
        <>
          <div className="money-form-field">
            <label
              className="money-form-field__label"
              htmlFor="area-leased-input"
            >
              Area leased (ha)
            </label>
            <input
              id="area-leased-input"
              type="number"
              min="0"
              step="0.01"
              className={`money-form-field__control money-form-field__control--input${
                farmValidationErrors.areaLeased ? ' is-error' : ''
              }`}
              value={areaLeased}
              onChange={(event) => onAreaLeasedChange(event.target.value)}
            />
          </div>

          <div className="money-form-field">
            <label className="money-form-field__label" htmlFor="lease-end-year">
              Lease end date (year)
            </label>
            <select
              id="lease-end-year"
              className={`money-form-field__control money-form-field__control--select${
                farmValidationErrors.leaseEndDate ? ' is-error' : ''
              }`}
              value={leaseEndDate}
              onChange={(event) => onLeaseEndDateChange(event.target.value)}
            >
              <option value="">Select year</option>
              {leaseYearOptions.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <fieldset
        className={`money-choice-group${
          farmValidationErrors.farmType ? ' is-error' : ''
        }`}
      >
        <legend className="money-choice-group__legend">
          Select your farm type
        </legend>
        <div className="money-choice-group__options money-choice-group__options--inline">
          {(['Irrigated', 'Dryland', 'Both'] as FarmTypeOption[]).map(
            (option) => {
              const checked = farmType === option;

              return (
                <label
                  key={option}
                  className={`money-choice money-choice--radio${
                    checked ? ' is-checked' : ''
                  }`}
                >
                  <input
                    type="radio"
                    className="money-choice__control"
                    name="farm-type"
                    value={option}
                    checked={checked}
                    onChange={() => onFarmTypeChange(option)}
                  />
                  <span className="money-choice__label-wrap">
                    <span className="money-choice__label">{option}</span>
                  </span>
                </label>
              );
            },
          )}
        </div>
      </fieldset>

      {(farmType === 'Irrigated' || farmType === 'Both') && (
        <div className="money-form-field">
          <label
            className="money-form-field__label"
            htmlFor="area-under-irrigation"
          >
            Area under irrigation (ha)
          </label>
          <input
            id="area-under-irrigation"
            type="number"
            min="0"
            step="0.01"
            className={`money-form-field__control money-form-field__control--input${
              farmValidationErrors.areaUnderIrrigation ? ' is-error' : ''
            }`}
            value={areaUnderIrrigation}
            onChange={(event) =>
              onAreaUnderIrrigationChange(event.target.value)
            }
          />
        </div>
      )}

      {(farmType === 'Dryland' || farmType === 'Both') && (
        <div className="money-form-field">
          <label className="money-form-field__label" htmlFor="area-of-dryland">
            Area of dryland (ha)
          </label>
          <input
            id="area-of-dryland"
            type="number"
            min="0"
            step="0.01"
            className={`money-form-field__control money-form-field__control--input${
              farmValidationErrors.areaOfDryLand ? ' is-error' : ''
            }`}
            value={areaOfDryLand}
            onChange={(event) => onAreaOfDryLandChange(event.target.value)}
          />
        </div>
      )}

      {(farmType === 'Irrigated' || farmType === 'Both') && (
        <fieldset className="money-choice-group">
          <legend className="money-choice-group__legend">Water sources</legend>
          <div className="money-choice-group__options money-choice-group__options--inline">
            {waterSourceOptions.map((option) => {
              const checked = selectedWaterSources.includes(option);

              return (
                <label
                  key={option}
                  className={`money-choice money-choice--checkbox${
                    checked ? ' is-checked' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    className="money-choice__control"
                    name="water-sources"
                    value={option}
                    checked={checked}
                    onChange={(event) =>
                      onWaterSourceToggle(option, event.target.checked)
                    }
                  />
                  <span className="money-choice__label-wrap">
                    <span className="money-choice__label">{option}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <fieldset
        className={`money-choice-group${
          farmValidationErrors.fullTimeFarming ? ' is-error' : ''
        }`}
      >
        <legend className="money-choice-group__legend">
          Is farming your full-time occupation?
        </legend>
        <div className="money-choice-group__options money-choice-group__options--inline">
          {yesNoOptions.map((option) => {
            const checked = fullTimeFarming === option;

            return (
              <label
                key={option}
                className={`money-choice money-choice--radio${
                  checked ? ' is-checked' : ''
                }`}
              >
                <input
                  type="radio"
                  className="money-choice__control"
                  name="full-time-farming"
                  value={option}
                  checked={checked}
                  onChange={() => onFullTimeFarmingChange(option)}
                />
                <span className="money-choice__label-wrap">
                  <span className="money-choice__label">{option}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="short-term-goals">
          Short-term goals
        </label>
        <textarea
          id="short-term-goals"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.shortTermGoals ? ' is-error' : ''
          }`}
          rows={3}
          value={shortTermGoals}
          onChange={(event) => onShortTermGoalsChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="long-term-goals">
          Long-term goals
        </label>
        <textarea
          id="long-term-goals"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.longTermGoals ? ' is-error' : ''
          }`}
          rows={3}
          value={longTermGoals}
          onChange={(event) => onLongTermGoalsChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="land-composition">
          Land composition
        </label>
        <textarea
          id="land-composition"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.landComposition ? ' is-error' : ''
          }`}
          rows={3}
          value={landComposition}
          onChange={(event) => onLandCompositionChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="workforce-number">
          Workforce number
        </label>
        <input
          id="workforce-number"
          type="number"
          min="0"
          step="1"
          className={`money-form-field__control money-form-field__control--input${
            farmValidationErrors.workforceNumber ? ' is-error' : ''
          }`}
          value={workforceNumber}
          onChange={(event) => onWorkforceNumberChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label className="money-form-field__label" htmlFor="worker-trust">
          Worker trust
        </label>
        <textarea
          id="worker-trust"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.workerTrust ? ' is-error' : ''
          }`}
          rows={3}
          value={workerTrust}
          onChange={(event) => onWorkerTrustChange(event.target.value)}
        />
      </div>

      <div className="money-form-field">
        <label
          className="money-form-field__label"
          htmlFor="employment-before-after"
        >
          Employment before &amp; after funding
        </label>
        <textarea
          id="employment-before-after"
          className={`money-form-field__control money-form-field__control--textarea${
            farmValidationErrors.employmentBeforeAfter ? ' is-error' : ''
          }`}
          rows={3}
          value={employmentBeforeAfter}
          onChange={(event) =>
            onEmploymentBeforeAfterChange(event.target.value)
          }
        />
      </div>
    </div>
  </form>
);
